import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Flex, Heading, Text, Input, InputGroup, InputLeftElement, InputRightElement,
  VStack, HStack, Badge, Button, Container, Icon,
  useClipboard, useToast, IconButton, Drawer, DrawerBody,
  DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton,
  useDisclosure, Tooltip, Table, Thead, Tbody, Tr, Th, Td,
  FormControl, FormLabel, Textarea, Image, SimpleGrid, AlertDialog,
  AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter,
  Divider, Card, CardBody, Spinner, useColorMode, useColorModeValue, List, ListItem,
  keyframes
} from '@chakra-ui/react';
import { 
  SearchIcon, ChevronRightIcon, CopyIcon, HamburgerIcon, 
  ArrowBackIcon, EditIcon, DeleteIcon, AddIcon, ViewIcon, 
  DownloadIcon, SunIcon, MoonIcon, CloseIcon
} from '@chakra-ui/icons';
import { 
  FaLightbulb, FaInfoCircle, FaExclamationTriangle, 
  FaBoxOpen, FaTimesCircle, FaTruck, FaCheckCircle, FaBook, 
  FaCog, FaFileUpload, FaFilePdf, FaSave, FaHome,
  FaWallet, FaHandshake, FaTags
} from 'react-icons/fa';
import { AIChatWidget } from '../components/AIChatWidget';
import {
  WikiEditor, WikiContentViewer, WikiDocActions, WikiTOC, WIKI_ATTACHMENT_MIME,
} from '../components/WikiEditor';
import WikiCategoryManager, { resolveWikiIcon } from '../components/WikiCategoryManager';
import {
  getWikiTopics,
  createWikiTopic,
  updateWikiTopic,
  deleteWikiTopic,
  deleteWikiAttachment,
  getWikiCategories,
  createWikiCategory
} from '../services/api';
import { CreatableSelect } from 'chakra-react-select';
import { useParams, useNavigate } from 'react-router-dom';

import TruckLoader from '../components/TruckLoader';

const TAGA_ORANGE = "#F68633";

const CATEGORY_THEMES = {
  'Destaques': 'purple',
  'Reclamações': 'red',
  'Devoluções': 'orange',
  'Cancelamento': 'pink',
  'Envios': 'cyan',
  'Pagamentos': 'green',
  'Pós-Venda': 'blue',
  'Geral': 'gray',
  'all': 'orange'
};

const CATEGORIES = [
  { id: 'all', label: 'Todos', icon: FaLightbulb },
  { id: 'Destaques', label: 'Destaques', icon: FaInfoCircle },
  { id: 'Geral', label: 'Geral', icon: FaBook },
  { id: 'Reclamações', label: 'Reclamações', icon: FaExclamationTriangle },
  { id: 'Devoluções', label: 'Devoluções', icon: FaBoxOpen },
  { id: 'Cancelamento', label: 'Cancelamentos', icon: FaTimesCircle },
  { id: 'Envios', label: 'Logística', icon: FaTruck },
  { id: 'Pagamentos', label: 'Pagamentos', icon: FaWallet },
  { id: 'Pós-Venda', label: 'Pós-Venda', icon: FaHandshake },
];

const SidebarItem = ({ cat, isActive, onClick }) => {
  const themeColor = cat.color || CATEGORY_THEMES[cat.id] || 'gray';
  
  const activeBg = useColorModeValue(`${themeColor}.100`, `${themeColor}.900`);
  const activeColor = useColorModeValue(`${themeColor}.700`, `${themeColor}.200`);
  const hoverBg = useColorModeValue(`${themeColor}.50`, 'whiteAlpha.100');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <Button
      variant="ghost"
      justifyContent="flex-start"
      leftIcon={<Icon as={cat.icon} boxSize={4} />} 
      onClick={onClick}
      w="full" 
      size="sm" 
      borderRadius="md" 
      mb={0.5} 
      bg={isActive ? activeBg : 'transparent'}
      color={isActive ? activeColor : textColor}
      borderLeft={isActive ? "3px solid" : "3px solid transparent"}
      borderColor={isActive ? `${themeColor}.500` : "transparent"}
      _hover={{ 
        transform: 'translateX(4px)', 
        bg: isActive ? activeBg : hoverBg,
        color: isActive ? activeColor : undefined
      }}
      fontSize="sm" 
      transition="all 0.3s ease"
    >
      <Text fontWeight={isActive ? "bold" : "medium"}>{cat.label}</Text>
    </Button>
  );
};

const DetailView = ({ item, onBack, onEdit, getTheme, onPrint }) => {
  const { onCopy, hasCopied } = useClipboard(item.standardResponse || '');
  const toast = useToast();
  const conteudoRef = useRef(null);
  const themeColor = getTheme ? getTheme(item.category) : (CATEGORY_THEMES[item.category] || 'gray');

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const headingColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const responseBg = useColorModeValue(`${themeColor}.50`, 'whiteAlpha.100');
  const responseTextColor = useColorModeValue('gray.800', 'white');
  const attachmentBg = useColorModeValue('gray.50', 'gray.700');
  const subCardBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  const handleCopy = () => {
    onCopy();
    toast({ title: "Copiado!", status: "success", duration: 1500, position: "top-right" });
  };

  const isPdf = (url) => url && (url.toLowerCase().includes('.pdf') || url.toLowerCase().includes('pdf'));

  return (
    <Flex maxW="100%" gap={6} align="flex-start" pb={10} data-wiki-print="area">
      <Box flex="1" minW={0} maxW={{ base: '100%', xl: '5xl' }} mx={{ base: 0, xl: 'auto' }}>
        <Flex justify="space-between" align="center" mb={4} gap={2} wrap="wrap" data-wiki-print="hide">
            <Button leftIcon={<ArrowBackIcon />} size="sm" variant="ghost" onClick={onBack} flexShrink={0}>Voltar</Button>
            <HStack spacing={2} flexWrap="wrap" justify="flex-end">
              <WikiDocActions item={item} colorScheme={themeColor} onPrint={onPrint} />
              {onEdit && (
                <Button leftIcon={<EditIcon />} size="sm" colorScheme={themeColor} variant="outline" onClick={() => onEdit(item)}>
                  Editar
                </Button>
              )}
            </HStack>
        </Flex>

        <Box bg={bgColor} borderRadius="xl" boxShadow="md" overflow="hidden" borderTop="4px solid" borderColor={`${themeColor}.400`}>
        <Box p={{ base: 4, md: 5 }} borderBottom="1px solid" borderColor={borderColor}>

          <HStack mb={2} flexWrap="wrap">
            <Badge colorScheme={themeColor} fontSize="xs" px={2} py={0.5} borderRadius="full">
              {item.category}
            </Badge>
            <Text fontSize="xs" color="gray.400" textTransform="uppercase">
              ID: #{item.id}
            </Text>
          </HStack>
          <Heading size={{ base: 'md', md: 'lg' }} color={headingColor}>{item.title}</Heading>
        </Box>

        <Box p={{ base: 4, md: 6 }}>
          {/* Em telas menores o sumário vem antes do texto, já que não há coluna lateral */}
          <Box display={{ base: 'block', xl: 'none' }} mb={5} data-wiki-print="hide">
            <WikiTOC content={item.content} containerRef={conteudoRef} colorScheme={themeColor} />
          </Box>

          <Box mb={6} ref={conteudoRef}>
            <Heading size="xs" textTransform="uppercase" mb={2} color={headingColor} display="flex" alignItems="center" data-wiki-print="hide">
              <Icon as={FaInfoCircle} mr={2} color={`${themeColor}.500`}/> Contexto & Instruções
            </Heading>
            {item.content ? (
              <WikiContentViewer content={item.content} />
            ) : (
              <Text fontSize="sm" color={textColor} lineHeight="1.6" whiteSpace="pre-wrap">
                {item.description || "Sem descrição adicional."}
              </Text>
            )}
          </Box>


          {item.standardResponse && (
            <>
              <Divider mb={6} borderColor={borderColor} />

              <Box>
                <Flex justify="space-between" align="center" mb={2}>
                  <Heading size="xs" textTransform="uppercase" color={headingColor} display="flex" alignItems="center">
                    <Icon as={FaCheckCircle} mr={2} color="green.400"/> Resposta para o Cliente
                  </Heading>
                  <Button
                    leftIcon={hasCopied ? <FaCheckCircle /> : <CopyIcon />}
                    colorScheme={hasCopied ? 'green' : themeColor}
                    size={{ base: 'sm', md: 'xs' }} onClick={handleCopy}
                    shadow="sm"
                  >
                    {hasCopied ? "Copiado!" : "Copiar Mensagem"}
                  </Button>
                </Flex>

                <Box
                  p={4}
                  bg={responseBg}
                  borderLeft="3px solid"
                  borderColor={`${themeColor}.400`}
                  borderRadius="md"
                  fontSize="sm"
                  fontFamily="monospace"
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                  overflowWrap="anywhere"
                  color={responseTextColor}
                >
                  {item.standardResponse}
                </Box>
              </Box>
            </>
          )}

          {item.attachments && item.attachments.length > 0 && (
            <Box mt={8}>
                <Heading size="xs" textTransform="uppercase" mb={3} color={headingColor}>Anexos</Heading>
                <SimpleGrid columns={{ base: 1 }} spacing={4}>
                  {item.attachments.map((att) => (
                    <Box key={att.id} borderRadius="md" overflow="hidden" border="1px solid" borderColor={borderColor} p={3} bg={attachmentBg}>
                      <Flex justify="space-between" align="center" mb={3}>
                          <HStack>
                            <Icon as={att.type === 'image' || !isPdf(att.url) ? ViewIcon : FaFilePdf} color={`${themeColor}.500`} />
                            <Text fontWeight="bold" fontSize="xs">{att.name}</Text>
                          </HStack>
                          <Button as="a" href={att.url} target="_blank" download size={{ base: 'sm', md: 'xs' }} leftIcon={<DownloadIcon />}>
                           Baixar
                          </Button>
                      </Flex>
                      
                      {isPdf(att.url) ? (
                        <Box h={{ base: '260px', md: '400px' }} w="100%">
                            <iframe src={att.url} width="100%" height="100%" title={att.name} style={{border: 'none', borderRadius: '4px'}} />
                        </Box>
                      ) : (
                        <Image src={att.url} alt={att.name} maxH="300px" objectFit="contain" mx="auto" borderRadius="sm" />
                      )}
                    </Box>
                  ))}
                </SimpleGrid>
            </Box>
          )}
        </Box>
        </Box>
      </Box>

      {/* Sumário fixo na lateral em telas largas; abaixo de xl ele é renderizado acima do texto */}
      <Box
        display={{ base: 'none', xl: 'block' }}
        w="240px"
        flexShrink={0}
        position="sticky"
        top="80px"
        data-wiki-print="hide"
      >
        <WikiTOC content={item.content} containerRef={conteudoRef} colorScheme={themeColor} />
      </Box>
    </Flex>
  );
};

const AttachmentDeleteAlert = ({ isOpen, onClose, onConfirm }) => (
    <AlertDialog isOpen={isOpen} onClose={onClose} isCentered>
      <AlertDialogOverlay>
        <AlertDialogContent borderRadius="xl">
          <AlertDialogHeader fontSize="lg" fontWeight="bold">Remover Anexo</AlertDialogHeader>
          <AlertDialogBody>
            Tem certeza que deseja remover este arquivo permanentemente?
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button onClick={onClose} size="sm">Cancelar</Button>
            <Button colorScheme="red" onClick={onConfirm} ml={3} size="sm">Remover</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
);

const TopicFormPage = ({ initialData, onSave, onCancel, categoryOptions, onCategoriesChanged }) => {
  const [formData, setFormData] = useState({
    id: initialData?.id || null,
    title: initialData?.title || '',
    category: initialData?.category || 'Geral',
    excerpt: initialData?.excerpt || '',
    description: initialData?.description || '',
    content: initialData?.content || null,
    standardResponse: initialData?.standardResponse || '',
    attachments: initialData?.attachments || [],
    files: []
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResponse, setShowResponse] = useState(!!initialData?.standardResponse);
  const [creatingCategory, setCreatingCategory] = useState(false);
  
  // Estado para o Modal de Exclusão de Anexo
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const { isOpen: isAttachAlertOpen, onOpen: onAttachAlertOpen, onClose: onAttachAlertClose } = useDisclosure();

  const fileInputRef = useRef();
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const responseBg = useColorModeValue('orange.50', 'whiteAlpha.100');
  const responseBorder = useColorModeValue('orange.200', 'orange.600');
  const dashedBorder = useColorModeValue('gray.300', 'gray.600');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const anexoBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const anexoHoverBg = useColorModeValue('orange.50', 'whiteAlpha.200');
  const novoAnexoBg = useColorModeValue('green.50', 'green.900');
  const novoAnexoText = useColorModeValue('green.700', 'green.200');

  const handleTriggerUpload = () => { fileInputRef.current.click(); };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      setFormData(prev => ({ ...prev, files: [...prev.files, ...selectedFiles] }));
      toast({ title: "Arquivo adicionado", status: "info", size: "xs", position: 'top-right' });
    }
  };

  const removeNewFile = (index) => {
    setFormData(prev => ({ ...prev, files: prev.files.filter((_, i) => i !== index) }));
  };

  const confirmRemoveExistingAttachment = (att) => {
      setAttachmentToDelete(att);
      onAttachAlertOpen();
  };

  const handleRemoveExistingAttachment = async () => {
    if (!attachmentToDelete) return;
    
    const success = await deleteWikiAttachment(attachmentToDelete.id);
    if (success) {
            setFormData(prev => ({ ...prev, attachments: prev.attachments.filter(att => att.id !== attachmentToDelete.id) }));
            toast({ title: "Removido.", status: "success", size: 'sm' });
    } else {
            toast({ title: "Erro ao remover anexo.", status: "error", size: 'sm' });
    }
    onAttachAlertClose();
    setAttachmentToDelete(null);
  };

  const handleSubmit = async () => {
    if (!formData.title) {
      toast({ title: "Informe o título", status: "error" });
      return;
    }
    setIsSubmitting(true);
    await onSave(formData);
    setIsSubmitting(false);
  };


  return (
    <Box maxW="5xl" mx="auto" pb={10}>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md" color={useColorModeValue('gray.700', 'white')}>
          {formData.id ? 'Editar' : 'Novo'}
        </Heading>
        <HStack>
          <Button variant="ghost" size="sm" onClick={onCancel} isDisabled={isSubmitting}>Cancelar</Button>
          <Button leftIcon={<FaSave />} size="sm" bg={TAGA_ORANGE} color="white" _hover={{ bg: 'orange.600' }} onClick={handleSubmit} isLoading={isSubmitting} loadingText="Salvando...">
            Salvar
          </Button>
        </HStack>
      </Flex>

      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6}>
        <Box gridColumn={{ lg: "span 2" }}>
          <Card variant="outline" bg={bgColor} shadow="sm" borderRadius="lg" mb={6}>
            <CardBody p={5}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Título</FormLabel>
                  <Input size="md" placeholder="Ex: Cliente alega falta de estoque" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
                </FormControl>

                {showResponse ? (
                  <FormControl>
                    <Flex justify="space-between" align="center">
                      <FormLabel fontSize="sm" fontWeight="bold" color={TAGA_ORANGE}>Resposta Padrão (texto para copiar)</FormLabel>
                      <IconButton
                        icon={<DeleteIcon />}
                        size={{ base: 'sm', md: 'xs' }}
                        variant="ghost"
                        colorScheme="red"
                        aria-label="Remover resposta padrão"
                        onClick={() => { setShowResponse(false); setFormData(prev => ({ ...prev, standardResponse: '' })); }}
                      />
                    </Flex>
                    <Textarea size="sm" rows={6} placeholder="Texto para copiar..." value={formData.standardResponse} onChange={e => setFormData({...formData, standardResponse: e.target.value})} bg={responseBg} borderColor={responseBorder} />
                  </FormControl>
                ) : (
                  <Button
                    leftIcon={<AddIcon />}
                    variant="outline"
                    size="sm"
                    w="full"
                    borderStyle="dashed"
                    color="gray.500"
                    fontWeight="normal"
                    onClick={() => setShowResponse(true)}
                  >
                    Adicionar Resposta Padrão (texto para copiar)
                  </Button>
                )}
              </VStack>
            </CardBody>
          </Card>
        </Box>

        <Box>
          <Card variant="outline" bg={bgColor} shadow="sm" borderRadius="lg" mb={6}>
            <CardBody p={5}>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">Categoria</FormLabel>
                  <CreatableSelect
                    size="sm"
                    inputId="wiki-categoria-select"
                    options={(categoryOptions || CATEGORIES.filter(c => c.id !== 'all' && c.id !== 'Destaques').map(c => c.id)).map(nome => ({ value: nome, label: nome }))}
                    value={{ value: formData.category, label: formData.category }}
                    onChange={(opt) => opt && setFormData(prev => ({ ...prev, category: opt.value }))}
                    onCreateOption={async (inputValue) => {
                      setCreatingCategory(true);
                      const res = await createWikiCategory({ nome: inputValue, icone: 'FaBook', cor: 'gray' });
                      setCreatingCategory(false);
                      if (res.success) {
                        setFormData(prev => ({ ...prev, category: res.data.nome }));
                        toast({ title: `Categoria "${res.data.nome}" criada!`, description: 'Ajuste ícone e cor em Gerenciar Base → Categorias.', status: 'success', position: 'top-right' });
                        onCategoriesChanged?.();
                      } else {
                        toast({ title: res.message, status: 'error', position: 'top-right' });
                      }
                    }}
                    isLoading={creatingCategory}
                    formatCreateLabel={(inputValue) => `Criar categoria "${inputValue}"`}
                    placeholder="Selecione ou digite..."
                    noOptionsMessage={() => 'Digite para criar uma categoria'}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Resumo</FormLabel>
                  <Textarea size="sm" rows={3} placeholder="Texto curto que aparece no card do tópico..." value={formData.excerpt} onChange={e => setFormData({...formData, excerpt: e.target.value})} />
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          <Card variant="outline" bg={bgColor} shadow="sm" borderRadius="lg" mb={6}>
            <CardBody p={5}>
              <FormLabel fontSize="sm">Anexos</FormLabel>
              <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} multiple />
              <Box border="2px dashed" borderColor={dashedBorder} borderRadius="md" p={4} textAlign="center" cursor="pointer" bg={inputBg} _hover={{ borderColor: TAGA_ORANGE }} onClick={handleTriggerUpload} mb={3}>
                <Icon as={FaFileUpload} boxSize={5} color="gray.400" mb={1} />
                <Text fontSize="xs" color={mutedText}>Clique para anexar</Text>
              </Box>
              <VStack align="stretch" spacing={2}>
                {formData.files.map((file, idx) => (
                  <Flex key={`new-${idx}`} justify="space-between" align="center" p={2} bg={novoAnexoBg} borderRadius="md">
                    <Text fontSize="xs" color={novoAnexoText} noOfLines={1}>{file.name} (Novo)</Text>
                    <IconButton aria-label="Remover arquivo" icon={<DeleteIcon />} size={{ base: 'sm', md: 'xs' }} colorScheme="red" variant="ghost" onClick={() => removeNewFile(idx)} />
                  </Flex>
                ))}
                {formData.attachments.map((att) => (
                  <Flex
                    key={`exist-${att.id}`}
                    justify="space-between"
                    align="center"
                    p={2}
                    bg={anexoBg}
                    borderRadius="md"
                    cursor="grab"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData(WIKI_ATTACHMENT_MIME, JSON.stringify({ url: att.url, name: att.name, type: att.type }));
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                    _active={{ cursor: 'grabbing' }}
                    _hover={{ bg: anexoHoverBg }}
                  >
                      <Text fontSize="xs" color={mutedText} noOfLines={1}>{att.name}</Text>
                      <IconButton aria-label="Remover anexo" icon={<DeleteIcon />} size={{ base: 'sm', md: 'xs' }} colorScheme="red" variant="ghost" onClick={() => confirmRemoveExistingAttachment(att)} />
                  </Flex>
                ))}
              </VStack>
              {formData.attachments.length > 0 && (
                <Text fontSize="xs" color={mutedText} mt={2}>
                  Arraste um anexo para dentro do documento para inseri-lo no texto.
                </Text>
              )}
            </CardBody>
          </Card>
        </Box>
      </SimpleGrid>

      <Card variant="outline" bg={bgColor} shadow="sm" borderRadius="lg" mb={6}>
        <CardBody p={5}>
          <FormControl>
            <FormLabel fontSize="sm">Conteúdo do Documento</FormLabel>
            <WikiEditor
              value={formData.content || formData.description}
              onChange={({ json, text }) => setFormData(prev => ({ ...prev, content: json, description: text }))}
              placeholder="Escreva o documento... Digite '/' para inserir títulos, tabelas, imagens, HTML e mais"
            />
          </FormControl>
        </CardBody>
      </Card>

      <AttachmentDeleteAlert
          isOpen={isAttachAlertOpen} 
          onClose={onAttachAlertClose} 
          onConfirm={handleRemoveExistingAttachment} 
      />
    </Box>
  );
};

const AdminPanel = ({ data, onEdit, onDelete, onView, onAdd, onBack, onManageCategories, getTheme }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const cardBorder = useColorModeValue('gray.100', 'gray.700');
  
  const [localSearch, setLocalSearch] = useState('');

  const filteredData = data.filter(item => item.title.toLowerCase().includes(localSearch.toLowerCase()));

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} wrap="wrap" gap={2}>
        <HStack flexShrink={0}>
           <Button leftIcon={<ArrowBackIcon />} size="sm" variant="ghost" onClick={onBack}>Voltar</Button>
           <Heading size="md">Gerenciar Base</Heading>
        </HStack>
        <HStack flexWrap="wrap" gap={2}>
          <Button leftIcon={<FaTags />} variant="outline" size="sm" onClick={onManageCategories}>
            Categorias
          </Button>
          <Button leftIcon={<AddIcon />} bg={TAGA_ORANGE} color="white" _hover={{bg: 'orange.600'}} size="sm" onClick={onAdd}>
            Novo
          </Button>
        </HStack>
      </Flex>

      <Card bg={bgColor} borderRadius="lg" boxShadow="sm">
        <Box p={4} borderBottom="1px solid" borderColor={cardBorder}>
            <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input 
                    placeholder="Filtrar tópicos..." 
                    value={localSearch}
                    onChange={(e) => setLocalSearch(e.target.value)}
                />
            </InputGroup>
        </Box>

        {/* só a tabela rola na horizontal; o filtro acima fica fixo */}
        <Box overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Título</Th>
              <Th>Categoria</Th>
              <Th>Anexos</Th>
              <Th isNumeric>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredData.map(item => (
              <Tr key={item.id} _hover={{ bg: hoverBg }}>
                <Td fontWeight="bold">{item.title}</Td>
                <Td>
                    <Badge colorScheme={getTheme ? getTheme(item.category) : (CATEGORY_THEMES[item.category] || 'gray')} fontSize="xs">{item.category}</Badge>
                </Td>
                <Td>{item.attachments?.length > 0 && <Badge fontSize="xs">{item.attachments.length}</Badge>}</Td>
                <Td isNumeric>
                  <HStack justify="flex-end" spacing={1}>
                    <IconButton aria-label="Visualizar" icon={<ViewIcon />} size={{ base: 'sm', md: 'xs' }} variant="ghost" onClick={() => onView(item)} />
                    <IconButton aria-label="Editar" icon={<EditIcon />} size={{ base: 'sm', md: 'xs' }} colorScheme="blue" variant="ghost" onClick={() => onEdit(item)} />
                    <IconButton aria-label="Excluir" icon={<DeleteIcon />} size={{ base: 'sm', md: 'xs' }} colorScheme="red" variant="ghost" onClick={() => onDelete(item)} />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        </Box>
        {filteredData.length === 0 && (
            <Box p={8} textAlign="center" color="gray.500">
                Nenhum registro encontrado.
            </Box>
        )}
      </Card>
    </Box>
  );
};

export default function WikiPage() {
  const { id: idDaRota } = useParams();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState('user');
  const [items, setItems] = useState([]);
  const [todosItens, setTodosItens] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dbCategories, setDbCategories] = useState([]);
  
  const [previousMode, setPreviousMode] = useState(null);

  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Destaques');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();
  const { isOpen: isAlertOpen, onOpen: onAlertOpen, onClose: onAlertClose } = useDisclosure();
  const { isOpen: isCatMgrOpen, onOpen: onCatMgrOpen, onClose: onCatMgrClose } = useDisclosure();
  const [itemToDelete, setItemToDelete] = useState(null);
  const drawerBtnRef = useRef();
  const toast = useToast();
  
  const { colorMode, toggleColorMode, setColorMode } = useColorMode();
  const bgMain = useColorModeValue('gray.50', 'gray.900');
  const sidebarBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const inputBg = useColorModeValue('white', 'gray.700');
  const headingColor = useColorModeValue('gray.700', 'white');
  const dropdownBg = sidebarBg;
  const dropdownHoverBg = useColorModeValue('orange.50', 'whiteAlpha.100');
  const dropdownBorder = useColorModeValue('gray.100', 'gray.700');
  const cardBg = sidebarBg; 
  const cardHoverTransform = 'translateY(-4px)';

  useEffect(() => { loadData(); }, [selectedCategory]);
  useEffect(() => { loadCategories(); loadArvore(); }, []);

  // Abre a página quando o id vem pela URL (link compartilhado) e mantém a
  // navegação funcionando ao usar voltar/avançar do navegador.
  useEffect(() => {
    // voltar do navegador saindo de /wiki/:id devolve para a listagem
    if (!idDaRota) {
      if (viewMode === 'detail') {
        setViewMode('user');
        setSelectedItem(null);
      }
      return;
    }

    if (!todosItens.length) return;
    if (selectedItem?.id === Number(idDaRota)) return;

    const alvo = todosItens.find((i) => i.id === Number(idDaRota));
    if (alvo) {
      setSelectedItem(alvo);
      setViewMode('detail');
    } else {
      toast({ title: 'Página não encontrada', status: 'warning', duration: 3000 });
      navigate('/wiki', { replace: true });
    }
  }, [idDaRota, todosItens]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCategories = async () => {
    const cats = await getWikiCategories();
    setDbCategories(cats || []);
  };

  // Lista completa (sem filtro de categoria) usada pela árvore, breadcrumb e select de pai
  const loadArvore = async () => {
    const data = await getWikiTopics('all');
    setTodosItens(data || []);
  };

  const categoriesList = dbCategories.length > 0
    ? [
        { id: 'all', label: 'Todos', icon: FaLightbulb },
        { id: 'Destaques', label: 'Destaques', icon: FaInfoCircle, color: 'purple' },
        ...dbCategories.map(c => ({ id: c.nome, label: c.nome, icon: resolveWikiIcon(c.icone), color: c.cor || 'gray' })),
      ]
    : CATEGORIES;

  const getTheme = (cat) => {
    const found = dbCategories.find(c => c.nome === cat);
    return found?.cor || CATEGORY_THEMES[cat] || 'gray';
  };

  const categoryOptions = dbCategories.length > 0
    ? dbCategories.map(c => c.nome)
    : null;

  const handleCategoriesChanged = async () => {
    await loadCategories();
    if (viewMode === 'admin') await loadAllData();
  };

  const loadData = async () => {
    setIsLoading(true);
    const categoryParam = selectedCategory === 'Destaques' ? 'all' : selectedCategory;
    const data = await getWikiTopics(categoryParam);
    setItems(data || []);
    setIsLoading(false);
  };

  // Carrega TODOS os itens (para o Admin)
  const loadAllData = async () => {
      setIsLoading(true);
      const data = await getWikiTopics('all');
      setItems(data || []);
      setIsLoading(false);
  };

  const handleSearch = async (value) => {
    setSearchTerm(value);
    if (value.length > 2) {
        setSearchLoading(true);
        setShowDropdown(true);
        const results = await getWikiTopics('all', value);
        setSearchResults(results || []);
        setSearchLoading(false);
    } else {
        setShowDropdown(false);
        setSearchResults([]);
    }
  };

  const clearSearch = () => {
      setSearchTerm('');
      setShowDropdown(false);
      setSearchResults([]);
  };

  const goHome = () => {
      setViewMode('user');
      setSelectedItem(null);
      if (idDaRota) navigate('/wiki');
      loadData();
  };

  const goAdmin = async () => {
      setViewMode('admin');
      setSelectedItem(null);
      await loadAllData();
  };

  const handleOpenDetail = (item) => {
    setPreviousMode(viewMode);
    setSelectedItem(item);
    setViewMode('detail');
    setShowDropdown(false);
    setSearchTerm('');
    if (item?.id && Number(idDaRota) !== item.id) navigate(`/wiki/${item.id}`);
  };


  // A classe no body liga o CSS de impressão. Fica restrita à wiki para não
  // interferir na impressão de outras telas (etiquetas, DANFE etc.).
  const prepararImpressao = () => document.body.classList.add('wiki-printing');

  // Cobre também o Ctrl+P do navegador, não só o botão "Exportar PDF"
  useEffect(() => {
    const aoImprimir = () => document.body.classList.add('wiki-printing');
    const aoTerminar = () => document.body.classList.remove('wiki-printing');

    window.addEventListener('beforeprint', aoImprimir);
    window.addEventListener('afterprint', aoTerminar);

    return () => {
      window.removeEventListener('beforeprint', aoImprimir);
      window.removeEventListener('afterprint', aoTerminar);
      document.body.classList.remove('wiki-printing');
    };
  }, []);

  const handleBack = () => {
    if (previousMode === 'admin') {
      goAdmin();
    } else {
      goHome();
    }
  };

  const handleOpenForm = (item = null) => {
    setSelectedItem(item); 
    setViewMode('form');
  };

  const handleSaveTopic = async (formData) => {
    let response;
    if (formData.id) {
      const dataToSend = { ...formData, new_files: formData.files };
      response = await updateWikiTopic(formData.id, dataToSend);
    } else {
      response = await createWikiTopic(formData);
    }

    if (response.success) {
        toast({ title: "Salvo com sucesso!", status: "success" });
        loadArvore();

        if(formData.id) {
             setSelectedItem(response.data); 
             setViewMode('detail');
        } else {
             goAdmin();
        }
    } else {
        toast({ title: "Erro ao salvar", description: response.message, status: "error" });
    }
  };

  const handleRequestDelete = (item) => {
    setItemToDelete(item);
    onAlertOpen();
  };

  const handleDeleteConfirm = async () => {
    if(!itemToDelete) return;
    const success = await deleteWikiTopic(itemToDelete.id);
    if (success) {
        setItems(prev => prev.filter(i => i.id !== itemToDelete.id));
        loadArvore();
        toast({ title: "Excluído.", status: "info" });
    } else {
        toast({ title: "Erro ao excluir.", status: "error" });
    }
    onAlertClose();
  };

  const handleExit = () => {
    setColorMode('light'); 
    setTimeout(() => { window.location.href = '/'; }, 100);
  };

  const filteredItemsUser = items.filter((item) => {
    if (selectedCategory === 'Destaques') return true;
    if (selectedCategory === 'all') return true;
    return item.category === selectedCategory;
  });

  return (
    <Flex minH="100vh" bg={bgMain}>
      <Flex 
        direction="column"
        justify="space-between"
        w={{ base: 0, md: '200px', lg: '240px' }}
        bg={sidebarBg} 
        borderRight="1px solid" 
        borderColor={borderColor}
        h="100vh" 
        position="sticky" 
        top={0} 
        display={{ base: 'none', md: 'flex' }}
        boxShadow="sm"
        zIndex="200"
        data-wiki-print="hide"
      >
        <Box p={4} flex="1" overflowY="auto">
          <Box mb={6} textAlign="center" bg="white" p={2} borderRadius="md" boxShadow="sm">
              <Image src="/TAGANOBG.png" alt="TAGA Logo" maxH="40px" mx="auto" fallbackSrc="https://via.placeholder.com/150x40?text=TAGA+LOGO" />
          </Box>
          
          <VStack align="stretch" spacing={0.5}>
            {categoriesList.map((cat) => (
              <SidebarItem
                key={cat.id}
                cat={cat}
                isActive={selectedCategory === cat.id && viewMode === 'user'}
                onClick={() => { goHome(); setSelectedCategory(cat.id); }}
              />
            ))}
          </VStack>

        </Box>

        <Box p={4} borderTop="1px solid" borderColor={borderColor}>
           <VStack spacing={2}>
             <Button w="full" leftIcon={<FaCog />} variant="outline" size="xs" onClick={goAdmin}>
               Gerenciar Base
             </Button>
             <Button w="full" leftIcon={<FaHome />} bg={TAGA_ORANGE} color="white" _hover={{ bg: 'orange.600' }} size="xs" onClick={handleExit}>
               Voltar para o ADM
             </Button>
           </VStack>
        </Box>
      </Flex>

      <Box flex="1" overflowY="auto" h="100vh" position="relative">
        <Container maxW="1200px" p={0}>
          <Box display={{ base: 'flex', md: 'none' }} p={4} bg={bgMain} position="sticky" top={0} zIndex="150" data-wiki-print="hide">
              <IconButton aria-label="Abrir menu da wiki" ref={drawerBtnRef} icon={<HamburgerIcon />} onClick={onDrawerOpen} />
          </Box>

          <Box
            position="sticky"
            // no mobile fica abaixo da barra do menu, que também é sticky
            top={{ base: '68px', md: '0' }}
            zIndex="100"
            data-wiki-print="hide"
            px={{ base: 4, md: 8 }} 
            pt={4}
            pb={4}
            bg={bgMain} 
            boxShadow={searchResults.length > 0 || searchTerm ? "md" : "none"}
          >
            <HStack spacing={3}>
                <InputGroup size="md">
                  <InputLeftElement pointerEvents="none">
                    {searchLoading ? <Spinner size="xs" color={TAGA_ORANGE} /> : <SearchIcon color="gray.400" />}
                  </InputLeftElement>
                  <Input 
                    placeholder="Pesquisar na Wiki..." 
                    bg={inputBg} 
                    borderRadius="md" 
                    boxShadow="sm"
                    border="1px solid"
                    borderColor="transparent"
                    _focus={{ borderColor: TAGA_ORANGE, boxShadow: 'sm' }}
                    value={searchTerm} 
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {searchTerm && (
                    <InputRightElement>
                        <IconButton aria-label="Limpar busca" icon={<CloseIcon />} size="sm" variant="ghost" borderRadius="full" onClick={clearSearch} />
                    </InputRightElement>
                  )}
                </InputGroup>
                
                <IconButton 
                    icon={colorMode === 'light' ? <MoonIcon /> : <SunIcon />} 
                    onClick={toggleColorMode} 
                    variant="solid" 
                    bg={inputBg}
                    aria-label="Mudar Tema" 
                    size="md"
                    boxShadow="sm"
                />
            </HStack>

            {showDropdown && searchResults.length > 0 && (
                <Box 
                    position="absolute" 
                    top="calc(100% - 5px)" 
                    left={{ base: 4, md: 8 }} 
                    right={{ base: 4, md: 8 }} 
                    bg={dropdownBg} 
                    mt={1} 
                    borderRadius="md" 
                    boxShadow="xl" 
                    zIndex="200"
                    maxH="300px" 
                    overflowY="auto"
                    border="1px solid"
                    borderColor={dropdownBorder}
                >
                    <List spacing={0}>
                        {searchResults.map(res => (
                            <ListItem 
                                key={res.id} 
                                p={3} 
                                cursor="pointer" 
                                _hover={{ bg: dropdownHoverBg }}
                                borderBottom="1px solid" 
                                borderColor={dropdownBorder}
                                onClick={() => handleOpenDetail(res)}
                            >
                                <Text fontWeight="bold" fontSize="sm">{res.title}</Text>
                                <Text fontSize="xs" color="gray.500" noOfLines={1}>{res.excerpt}</Text>
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}
          </Box>
            
          <Box px={{ base: 4, md: 8 }} pb={10}>
          {isLoading && viewMode !== 'detail' && viewMode !== 'form' ? (
             <TruckLoader />
          ) : (
            <>
              {viewMode === 'admin' && (
                <AdminPanel
                   data={items}
                   onAdd={() => handleOpenForm()}
                   onEdit={handleOpenForm}
                   onView={handleOpenDetail}
                   onDelete={handleRequestDelete}
                   onBack={goHome}
                   onManageCategories={onCatMgrOpen}
                   getTheme={getTheme}
                />
              )}

              {viewMode === 'form' && (
                <TopicFormPage
                  initialData={selectedItem}
                  onSave={handleSaveTopic}
                  onCancel={() => selectedItem ? setViewMode('detail') : goAdmin()}
                  categoryOptions={categoryOptions}
                  onCategoriesChanged={loadCategories}
                />
              )}

              {viewMode === 'detail' && selectedItem && (
                <DetailView
                  item={selectedItem}
                  onBack={handleBack}
                  onEdit={handleOpenForm}
                  getTheme={getTheme}
                  onPrint={prepararImpressao}
                />
              )}

              {viewMode === 'user' && (
                <Box>
                  <Flex justify="space-between" align="center" mb={4}>
                     <Heading size="sm" color={headingColor}>
                        {selectedCategory === 'all' ? 'Destaques & Tópicos' : selectedCategory}
                     </Heading>
                  </Flex>
                  
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                    {filteredItemsUser.length > 0 ? (
                      filteredItemsUser.map(item => {
                          const catTheme = getTheme(item.category);
                          return (
                            <Card 
                              key={item.id} 
                              bg={cardBg} 
                              borderRadius="lg" 
                              boxShadow="sm"
                              cursor="pointer"
                              borderTop="3px solid" 
                              borderTopColor={`${catTheme}.400`}
                              onClick={() => handleOpenDetail(item)}
                              transition="all 0.2s"
                              _hover={{ transform: cardHoverTransform, boxShadow: 'md' }}
                            >
                              <CardBody p={4}>
                                 <Badge colorScheme={catTheme} mb={2} fontSize="xs">{item.category}</Badge>
                                 <Heading size="sm" mb={2} noOfLines={2} fontSize="sm">{item.title}</Heading>
                                 <Text color="gray.500" fontSize="xs" noOfLines={3}>{item.excerpt}</Text>
                              </CardBody>
                            </Card>
                          )
                      })
                    ) : (
                      <Text color="gray.500" fontSize="sm">Nenhum tópico encontrado.</Text>
                    )}
                  </SimpleGrid>
                </Box>
              )}
            </>
          )}
          </Box>
        </Container>
      </Box>

      <Box data-wiki-print="hide">
        <AIChatWidget onOpenTopic={handleOpenDetail} />
      </Box>

      <WikiCategoryManager
        isOpen={isCatMgrOpen}
        onClose={onCatMgrClose}
        categories={dbCategories}
        onChanged={handleCategoriesChanged}
      />

      <AlertDialog isOpen={isAlertOpen} onClose={onAlertClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Excluir Tópico</AlertDialogHeader>
            <AlertDialogBody>Confirmar exclusão?</AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={onAlertClose} size="sm">Não</Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3} size="sm">Sim</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Drawer isOpen={isDrawerOpen} placement="left" onClose={onDrawerClose} finalFocusRef={drawerBtnRef}>
        <DrawerOverlay />
        <DrawerContent>
           <DrawerCloseButton />
           <DrawerHeader>
                <Box bg="white" p={2} borderRadius="md" display="inline-block">
                    <Image src="/TAGANOBG.png" h="40px" fallbackSrc="https://via.placeholder.com/150x50?text=TAGA" />
                </Box>
           </DrawerHeader>
           <DrawerBody>
             <VStack align="stretch">
                {categoriesList.map((cat) => (
                  <SidebarItem key={cat.id} cat={cat} isActive={selectedCategory === cat.id} onClick={() => { goHome(); setSelectedCategory(cat.id); onDrawerClose(); }} />
                ))}


                <Divider my={4} />
                <Button onClick={() => { goAdmin(); onDrawerClose(); }}>Gerenciar</Button>
                <Button size="sm" onClick={handleExit} mt={2} bg={TAGA_ORANGE} color="white">Voltar ADM</Button>
             </VStack>
           </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
}